import { InvoiceCommercialResponse } from "@/api";
import React, { useState } from "react";


export interface InvoiceCommercialEditProps {
  value: InvoiceCommercialResponse;

  onChange: (
    value: InvoiceCommercialResponse,
  ) => void;
}

export default function InvoiceCommercialEdit({
  value,
  onChange,
}: InvoiceCommercialEditProps) {
  const [error, setError] =
    useState<string | null>(null);

  const update = <
    K extends keyof InvoiceCommercialResponse
  >(
    field: K,
    fieldValue: InvoiceCommercialResponse[K],
  ) => {
    onChange({
      ...value,
      [field]: fieldValue,
    });
  };

  return (
    <section className="rounded-xl border bg-white p-6">

      <div className="grid gap-8 lg:grid-cols-2">
        {/* Summary */}

        <div>

          <div className="space-y-3">
            <SummaryRow
              label="Currency"
              value={
                value.currency ?? "INR"
              }
              isCurrency={false}
            />

            <SummaryRow
              label="Subtotal"
              value={
                Number(value.subtotal_amount)
              }
            />

            <SummaryRow
              label="Items Discount"
              value={
                Number(value.items_discount_amount)
              }
            />

            <SummaryRow
              label="Commercial Discount"
              value={
                Number(value.commercial_discount_amount)
              }
            />

            <SummaryRow
              label="Total Discount"
              value={
                Number(value.total_discount_amount)
              }
            />

            <SummaryRow
              label="Tax"
              value={Number(value.tax_amount)}
            />

            <SummaryRow
              label="Round Off"
              value={
                Number(value.round_off_amount)
              }
            />

            <div className="border-t pt-3">
              <SummaryRow
                label="Final Amount"
                value={
                  Number(value.final_amount)
                }
                emphasized
              />
            </div>

            <SummaryRow
              label="Valid Until"
              value={
                value.valid_until ?? "—"
              }
              isCurrency={false}
            />
          </div>
        </div>

        {/* Editable */}

        <div>
          <h3 className="mb-4 text-sm font-semibold text-gray-700">
            Commercial Adjustments
          </h3>

          <div className="space-y-4">
            <NumberInput
              label="Commercial Discount"
              value={
                Number(value.commercial_discount_amount) ??
                0
              }
              onChange={(v) =>
                update(
                  "commercial_discount_amount",
                  v.toString(),
                )
              }
            />

            <NumberInput
              label="Round Off"
              value={Number(value.round_off_amount ?? 0)}
              onChange={(v) =>
                update(
                  "round_off_amount",
                  v.toString(),
                )
              }
            />

            <div>
              <label className="mb-1 block text-sm font-medium">
                Payment Mode
              </label>

              <select
                value={
                  value.payment_mode ?? ""
                }
                onChange={(event) =>
                  update(
                    "payment_mode",
                    event.target.value,
                  )
                }
                className="w-full rounded-md border px-3 py-2"
              >
                <option value="">
                  Select payment mode
                </option>

                <option value="BANK_TRANSFER">
                  Bank Transfer
                </option>

                <option value="ONLINE">
                  Online
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="mt-4 text-sm text-red-600">
          {error}
        </div>
      )}
    </section>
  );
}

function NumberInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium">
        {label}
      </label>

      <input
        type="number"
        value={value}
        onChange={(event) =>
          onChange(
            Number(event.target.value) || 0,
          )
        }
        className="w-full rounded-md border px-3 py-2"
      />
    </div>
  );
}

function SummaryRow({
  label,
  value,
  isCurrency = true,
  emphasized = false,
}: {
  label: string;
  value?: number | string;
  isCurrency?: boolean;
  emphasized?: boolean;
}) {
  const displayValue =
    value == null
      ? "—"
      : isCurrency
        ? `₹${Number(value).toFixed(2)}`
        : String(value);

  return (
    <div className="flex items-center justify-between gap-4">
      <span
        className={
          emphasized
            ? "text-sm font-semibold text-gray-900"
            : "text-sm text-gray-500"
        }
      >
        {label}
      </span>

      <span
        className={
          emphasized
            ? "text-base font-semibold text-gray-900"
            : "text-sm font-medium text-gray-900"
        }
      >
        {displayValue}
      </span>
    </div>
  );
}